import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Button, Space, Spin, message, Checkbox } from "antd";
import { useGetIsPhone } from "@extensions";
import { serverFetch } from "@extensions";
import { useUser } from "@redux";
import { useSelector } from "react-redux";
import "./payment.css";
import { fmtDate, ftmPrice } from "extensions";

const Payment = ({ sales, open, onClose }) => {
  const isPhone = useGetIsPhone();
  const user = useUser();
  const company = useSelector((s) => s.company);
  const [paymentVids, setPaymentVids] = useState([]);
  const [loadingPaymentVids, setLoadingPaymentVids] = useState(false);
  const [selectedSales, setSelectedSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userAgreementChecked, setUserAgreementChecked] = useState(false);
  const [rulesChecked, setRulesChecked] = useState(false);

  useEffect(() => {
    if (open && window.self !== window.top) {
      window.parent.postMessage({ sender: "proffit", height: "800px" }, "*");
    }
  }, [open]);

  // Инициализация выбранных записей при открытии формы
  useEffect(() => {
    if (open && sales && sales.length > 0) {
      setSelectedSales(sales.map(sale => sale.id));
    } else {
      setSelectedSales([]);
    }
    // Сбрасываем согласия при открытии формы
    setUserAgreementChecked(false);
    setRulesChecked(false);
  }, [open, sales]);

  // Определяем, какие согласия нужны
  const needsUserAgreement = useMemo(() => {
    return !!company?.userAgreementUrl;
  }, [company]);

  const needsRules = useMemo(() => {
    return !!company?.rulesUrl;
  }, [company]);

  // Проверяем, все ли необходимые согласия даны
  const allAgreementsAccepted = useMemo(() => {
    if (needsUserAgreement && !userAgreementChecked) return false;
    if (needsRules && !rulesChecked) return false;
    return true;
  }, [needsUserAgreement, needsRules, userAgreementChecked, rulesChecked]);

  const selectedSalesData = useMemo(() => {
    return sales?.filter(sale => selectedSales.includes(sale.id)) || [];
  }, [sales, selectedSales]);

  const totalAmount = useMemo(() => {
    return selectedSalesData.reduce((sum, sale) => sum + (sale.syma || 0), 0);
  }, [selectedSalesData]);


  useEffect(() => {
    if (open && user) {
      fetchPaymentVids();
    }
  }, [open, user]);

  const fetchPaymentVids = async () => {
    if (!user) return;

    setLoadingPaymentVids(true);
    try {
      const data = await serverFetch("OnlineClient/PaymentVids", { method: "GET" }, user);
      setPaymentVids(data || []);
    } catch (error) {
      console.error("Ошибка получения видов оплат:", error);
      message.error(error?.UserMessage || "Не удалось загрузить виды оплат");
      setPaymentVids([]);
    } finally {
      setLoadingPaymentVids(false);
    }
  };

  const handlePay = async (vid) => {
    if (!user || selectedSales.length === 0) {
      return;
    }

    setLoading(true);
    try {
      const queryParams = {
        ids: selectedSales,
        vid: vid,
      };

      const result = await serverFetch("OnlineClient/Pay", { method: "GET", queryParams: queryParams }, user);
      
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Ошибка при оплате:", error);
      message.error(error.userMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer title="Оплата" placement="right" onClose={onClose} open={open} width={isPhone ? "100%" : 540} closable={true} className={`payment-drawer ${isPhone ? "payment-drawer-mobile" : ""}`}>
      <div className="payment-content">
        {sales && sales.length > 0 ? (
          <>
            <div className="payment-list">
              {sales.map((sale) => (
                <div key={sale.id} className="payment-item">
                  <Checkbox
                    checked={selectedSales.includes(sale.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSales([...selectedSales, sale.id]);
                      } else {
                        setSelectedSales(selectedSales.filter(id => id !== sale.id));
                      }
                    }}
                    className="payment-item-checkbox"
                  />
                  <div className="payment-item-info">
                    <div className="payment-item-date">{fmtDate(sale.docDate)}</div>
                    {sale.productName && (<div className="payment-item-product">{sale.productName}</div>)}
                  </div>
                  <div className="payment-item-price">{ftmPrice(sale.syma)} руб.</div>
                </div>
              ))}
            </div>
            <div className="payment-total">
              <div className="payment-total-label">Общая сумма:</div>
              <div className="payment-total-amount">{ftmPrice(totalAmount)} руб.</div>
            </div>
            {(needsUserAgreement || needsRules) && (
              <div className="payment-agreements">
                {needsUserAgreement && (
                  <Checkbox
                    checked={userAgreementChecked}
                    onChange={(e) => setUserAgreementChecked(e.target.checked)}
                    className="payment-agreement-checkbox"
                  >
                    Я согласен с{" "}
                    <a href={company.userAgreementUrl} target="_blank" rel="noreferrer">
                      Пользовательским соглашением
                    </a>
                  </Checkbox>
                )}
                {needsRules && (
                  <Checkbox
                    checked={rulesChecked}
                    onChange={(e) => setRulesChecked(e.target.checked)}
                    className="payment-agreement-checkbox"
                  >
                    Я согласен с{" "}
                    <a href={company.rulesUrl} target="_blank" rel="noreferrer">
                      Условиями и правилами
                    </a>
                  </Checkbox>
                )}
              </div>
            )}
            {loadingPaymentVids ? (
              <div className="payment-vids-loading"><Spin /></div>
            ) : (
              <div className="payment-vids">
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {paymentVids.map((vid) => (
                    <Button
                      key={vid.id}
                      type="primary"
                      block
                      className="payment-vid-button"
                      disabled={totalAmount === 0 || loading || !allAgreementsAccepted}
                      loading={loading}
                      onClick={() => handlePay(vid.id)}
                    >
                      {vid.name}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </>
        ) : (
          <div className="payment-empty">Нет товаров для оплаты</div>
        )}
      </div>
    </Drawer>
  );
};

export default Payment;

